<template>
  <div>
    <!-- Loader -->
    <loading-panel :loading="loading"></loading-panel>

    <card>
      <!-- Tabs and Actions Header -->
      <div class="row mb-3">
        <div class="col">
          <div
            class="btn-group btn-group-toggle"
            :class="'float-left'"
            data-toggle="buttons"
          >
            <label
              v-for="(option, index) in categories"
              :key="index + j"
              class="btn btn-sm btn-primary btn-simple"
              :class="{ active: option.active }"
              :id="index"
            >
              <input
                type="radio"
                @click="switchTab(index)"
                name="options"
                autocomplete="off"
                :checked="option.active"
              />
              <span style="font-size: medium; font-weight: lighter" class="d-none d-sm-block">{{ option.name }}</span>
              <span class="d-block d-sm-none">
                <i :class="option.icon"></i>
              </span>
            </label>
          </div>
        </div>
        <div class="col text-right d-sm-block d-none" v-if="activeTabIndex === 0">
          <base-button @click="createNewPage()" type="primary" size="sm">
            <i class="tim-icons mr-1 el-icon-plus"></i> Create POS page
          </base-button>
        </div>
        <div class="col text-right d-sm-none d-block" v-if="activeTabIndex === 0">
          <base-button @click="createNewPage()" type="primary" size="sm">
            <i class="tim-icons el-icon-plus"></i>
          </base-button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="paginatedPages.length === 0 && !loading" class="pp-empty-state mt-4">
        <img src="/img/pandas/pos.png" alt="No POS pages" class="empty-state-img" />
        <h4 class="empty-state-text" v-if="activeTabIndex === 0">No POS pages yet</h4>
        <h4 class="empty-state-text" v-else>No archived pages</h4>
        <p class="pp-empty-state__hint" v-if="activeTabIndex === 0">
          POS pages control how customers see and complete an in-venue payment on a POS tablet or card reader
        </p>
      </div>

      <!-- Card list -->
      <div v-if="paginatedPages.length > 0" class="pp-list mt-3">
        <div
          v-for="page in paginatedPages"
          :key="'PP' + page.id"
          class="pp-card"
          :class="{ 'pp-card--archived': page.archived === 1 }"
          @click="configurePage(page.id)"
        >
          <div class="pp-card__body">
            <!-- Left: gradient swatch -->
            <div class="pp-card__swatch"
                 :style="{ background: swatchBackground(page) }">
              <span class="pp-card__swatch-initials">{{ swatchInitials(page.name) }}</span>
            </div>

            <!-- Center: name + metadata -->
            <div class="pp-card__info">
              <div class="pp-card__label text-white">{{ page.name || 'Untitled page' }}</div>

              <!-- Row 1: page type + inline detail chips -->
              <div class="pp-card__meta">
                <span class="pp-card__type-name">{{ typeLabel(page.type) }}</span>
                <span v-for="(detail, i) in subtitleDetails(page)"
                      :key="'D' + page.id + '-' + i"
                      class="pp-card__meta-detail">
                  {{ detail }}
                </span>
              </div>

              <!-- Row 2: last modified -->
              <div class="pp-card__usage" v-if="page.updated_at">
                <span class="pp-card__usage-label">Last updated</span>
                <span class="pp-card__usage-text">{{ getRelativeTime(page.updated_at) }}</span>
              </div>
            </div>

            <!-- Right: badges + actions -->
            <div class="pp-card__actions">
              <el-tooltip v-if="page.type !== 'pos' && page.is_default === 1" content="Used by default for new payments of this type" placement="top">
                <span class="badge-tag badge-tag--primary">main</span>
              </el-tooltip>
              <span v-if="page.archived === 1" class="badge-tag badge-tag--archived">Archived</span>

              <el-dropdown @click.native.stop>
                <base-button simple type="primary" link size="sm">
                  <i class="tim-icons el-icon-more"></i>
                </base-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <a v-if="activeTabIndex === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('configure', page)">
                      <i class="tim-icons icon-pencil mr-2"></i> Edit page
                    </a>
                    <a v-if="activeTabIndex === 0 && page.type !== 'pos' && page.is_default !== 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('setDefault', page)">
                      <i class="tim-icons icon-check-2 mr-2"></i> Set as main
                    </a>
                    <a v-if="activeTabIndex === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('duplicate', page)">
                      <i class="tim-icons icon-single-copy-04 mr-2"></i> Duplicate
                    </a>
                    <hr v-if="activeTabIndex === 0">
                    <a v-if="activeTabIndex === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('archive', page)">
                      <i class="tim-icons icon-trash-simple mr-2"></i> Archive
                    </a>
                    <a v-if="activeTabIndex === 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('restore', page)">
                      <i class="tim-icons icon-refresh-02 mr-2"></i> Restore
                    </a>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="row" v-if="totalRows > perPage">
        <div class="col d-flex justify-content-center">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
          ></b-pagination>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import { API_LOCATION } from "@/api-config";
import axios from "axios";
import swal from "sweetalert2";
import { Auth } from "aws-amplify";
import { BPagination } from "bootstrap-vue";
import { Dropdown, DropdownMenu, Tooltip } from "element-ui";
import moment from "moment";

export default {
  name: "PaymentPagesList",
  components: {
    BPagination,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [Tooltip.name]: Tooltip,
  },
  data() {
    return {
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      loading: false,
      paymentPages: [],
      filteredPages: [],
      j: 0,
      activeTabIndex: 0,
      categories: [
        { name: 'Active', icon: 'tim-icons icon-check-2', active: true },
        { name: 'Archived', icon: 'tim-icons icon-trash-simple', active: false }
      ],
      // POS-only for the beta. Payment requests (type 'link') are suppressed
      // here and will live under a separate "Invoicing" feature later — they
      // share the payment_pages table but never the same menu. Re-add 'link'
      // when invoicing ships.
      visibleTypes: [
        { type: 'pos', label: 'POS' },
        // { type: 'link', label: 'Payment request' }, // moves to Invoicing
        // { type: 'invoice', label: 'Invoice' },       // hidden until invoice phase
      ],
      currentPage: 1,
      perPage: 10,
      totalRows: 0
    }
  },
  computed: {
    paginatedPages() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredPages.slice(start, start + this.perPage)
    }
  },
  methods: {
    switchTab(index) {
      this.activeTabIndex = index
      for (let n in this.categories) {
        this.categories[n].active = false
      }
      this.categories[index].active = true
      this.j++
      this.currentPage = 1
      this.filterPages()
    },
    filterPages() {
      const visibleTypeKeys = this.visibleTypes.map(t => t.type)
      const base = this.paymentPages.filter(p => visibleTypeKeys.includes(p.type))
      if (this.activeTabIndex === 0) {
        this.filteredPages = base.filter(p => (p.archived || 0) !== 1)
      } else {
        this.filteredPages = base.filter(p => (p.archived || 0) === 1)
      }
      this.totalRows = this.filteredPages.length
    },
    typeLabel(type) {
      const match = this.visibleTypes.find(t => t.type === type)
      return match ? match.label : type
    },
    swatchInitials(name) {
      return (name || '?')
        .split(/\s+/)
        .map(w => w.charAt(0).toUpperCase())
        .join('')
        .slice(0, 2)
    },
    swatchBackground(page) {
      const accent = page.button_color || '#e14eca'
      const surface = page.background_color || '#27293d'
      return `linear-gradient(135deg, ${accent}, ${surface})`
    },
    subtitleDetails(page) {
      const parts = []
      if (page.type === 'link') {
        if (page.payment_request_has_timeout) {
          const period = ['Hour', 'Day', 'Week', 'Month'][page.payment_request_timeout_period] || 'Day'
          parts.push(`Expires in ${page.payment_request_timeout_value} ${period.toLowerCase()}${page.payment_request_timeout_value === 1 ? '' : 's'}`)
        } else {
          parts.push('No expiration')
        }
      }
      if (page.type === 'pos') {
        parts.push(page.signature_required ? 'Signature required' : 'Signature optional')
      }
      parts.push(page.allow_tipping ? 'Tipping enabled' : 'No tipping')
      return parts
    },
    pageSubtitle(page) {
      return this.subtitleDetails(page).join(' · ')
    },
    getRelativeTime(dateString) {
      if (!dateString) return '-'
      const now = moment()
      const inputDate = moment(dateString)
      const diffInDays = now.diff(inputDate, 'days')
      const diffInWeeks = now.diff(inputDate, 'weeks')
      const diffInMonths = now.diff(inputDate, 'months')

      if (diffInDays < 1) return 'Today'
      if (diffInDays === 1) return 'Yesterday'
      if (diffInDays < 7) return `${diffInDays} days ago`
      if (diffInWeeks === 1) return 'Last week'
      if (diffInWeeks < 4) return `${diffInWeeks} weeks ago`
      if (diffInMonths === 1) return 'Last month'
      return `${diffInMonths} months ago`
    },
    handleAction(command, item) {
      if (command === 'configure') {
        this.configurePage(item.id)
      } else if (command === 'archive') {
        this.$notify({ type: 'info', message: `Archive "${item.name}" — not yet wired up` })
      } else if (command === 'restore') {
        this.$notify({ type: 'info', message: `Restore "${item.name}" — not yet wired up` })
      } else if (command === 'setDefault') {
        this.$notify({ type: 'info', message: `Set "${item.name}" as main — not yet wired up` })
      } else if (command === 'duplicate') {
        this.$notify({ type: 'info', message: `Duplicate "${item.name}" — not yet wired up` })
      }
    },
    configurePage(id) {
      this.$router.push({ path: `/purchases/point-of-sale/${id}` })
    },
    createNewPage() {
      this.$router.push({ path: '/purchases/point-of-sale/new' })
    },
    getAuthToken() {
      return new Promise((resolve) => {
        Auth.currentSession()
          .then(data => resolve(data.idToken.jwtToken))
          .catch(err => console.log(err))
      })
    },
    async getPaymentPages() {
      this.loading = true
      try {
        const token = await this.getAuthToken()
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/payment-pages`
        const response = await axios.get(url, { headers: { Authorization: token } })
        this.paymentPages = response.data.payment_pages || []
        this.filterPages()
      } catch (err) {
        console.log(err)
        const status = err.response && err.response.status
        if (status === 401) {
          swal({
            title: 'Error',
            html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
            type: 'error',
          }).then(() => {
            this.$router.push('/')
          })
        } else if (status === 403) {
          swal({
            title: 'Error',
            html: 'You do not have permission to view payment pages.',
            type: 'error',
          })
        } else if (status === 503) {
          swal('Service slow', 'Please try again in a moment.', 'warning')
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    this.getPaymentPages()
  }
}
</script>

<style scoped>
/* Empty state */
.pp-empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
}
.pp-empty-state__hint {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  max-width: 480px;
  margin: 8px auto 0;
}

/* Card list */
.pp-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pp-card {
  background: rgba(128, 128, 128, 0.05);
  border-left: 3px solid #e14eca;
  border-radius: 0.375rem;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.pp-card:hover {
  background: rgba(128, 128, 128, 0.12);
}
.pp-card--archived {
  opacity: 0.5;
  border-left-color: rgba(125, 125, 125, 0.4);
}
.pp-card__body {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* Gradient swatch — replaces type-icon slot */
.pp-card__swatch {
  flex-shrink: 0;
  width: 48px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pp-card__swatch-initials {
  font-size: 0.8rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.03em;
}

.pp-card__info {
  flex: 1;
  min-width: 0;
}
.pp-card__label {
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pp-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 3px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
}
.pp-card__type-name {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  font-size: 10px;
}
.pp-card__meta-detail {
  display: inline-flex;
  align-items: center;
}

/* Usage row — subordinate to meta row above */
.pp-card__usage {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
}
.pp-card__usage-label {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
}
.pp-card__usage-text {
  color: rgba(255, 255, 255, 0.75);
}

.pp-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* Badges */
/* Mobile: collapse meta row, hide "Last updated" label to save space */
@media (max-width: 575px) {
  .pp-card__body {
    gap: 10px;
  }
  .pp-card__swatch {
    width: 40px;
    height: 36px;
  }
  .pp-card__usage-label {
    display: none;
  }
}
</style>

<style>
.white-content .pp-empty-state__hint {
  color: rgba(0, 0, 0, 0.6);
}
.white-content .pp-card {
  background: rgba(0, 0, 0, 0.02);
}
.white-content .pp-card:hover {
  background: rgba(0, 0, 0, 0.05);
}
.white-content .pp-card__meta {
  color: rgba(0, 0, 0, 0.7);
}
.white-content .pp-card__usage {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .pp-card__usage-label {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .pp-card__usage-text {
  color: rgba(0, 0, 0, 0.7);
}
</style>
